import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Hammer, Home, Link2Off } from "lucide-react";
import { toast } from "sonner";

/**
 * Shows all renovation/capex transactions linked to a real-estate property,
 * with totals by year. Lets the user unlink incorrectly-tagged txs.
 */
export default function RenovationsDialog({ open, onOpenChange, property }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = React.useCallback(async () => {
    if (!property?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/investments/${property.id}/renovations`);
      setData(r.data);
    } catch (e) {
      logger.error("renovations load", e);
    } finally {
      setLoading(false);
    }
  }, [property?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const unlink = async (txId) => {
    try {
      await api.patch(`/transactions/${txId}`, { linked_property_id: "" });
      toast.success("Vínculo eliminado");
      load();
    } catch {
      toast.error("No se pudo desvincular");
    }
  };

  const currency = data?.currency || property?.currency || "EUR";
  const years = data ? Object.entries(data.by_year || {}).sort((a, b) => b[0].localeCompare(a[0])) : [];

  const availableCategories = React.useMemo(() => {
    const set = new Set();
    (data?.items || []).forEach((t) => { if (t.category) set.add(t.category); });
    return Array.from(set).sort();
  }, [data]);

  const filteredItems = React.useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((t) => {
      if (yearFilter !== "all" && !(t.date || "").startsWith(yearFilter)) return false;
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      return true;
    });
  }, [data, yearFilter, categoryFilter]);

  const filteredTotal = React.useMemo(() => (
    filteredItems.filter((t) => (t.amount || 0) < 0)
      .reduce((s, t) => s + Math.abs(t.amount), 0)
  ), [filteredItems]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Hammer className="w-5 h-5 text-[#8B6B1F]" />
            Reformas · {property?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-6 text-sm text-[#5F7368]">Cargando…</div>
        ) : !data ? (
          <div className="p-6 text-sm text-[#8A9992]">Sin datos</div>
        ) : (
          <>
            {/* Totals bar */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-[#EBEBEA] p-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Precio compra</div>
                <div className="font-display text-base font-semibold tabular-nums text-[#0A110E]">
                  {formatCurrency(data.purchase_price, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-[#C89A3F]/25 bg-[#C89A3F]/5 p-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8B6B1F]">Capex acumulado</div>
                <div className="font-display text-base font-semibold tabular-nums text-[#8B6B1F]" data-testid="renovations-total-capex">
                  {formatCurrency(data.total_capex, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-[#1C7C54]/25 bg-[#1C7C54]/5 p-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#1C7C54]">Cost basis</div>
                <div className="font-display text-base font-semibold tabular-nums text-[#1C7C54]" data-testid="renovations-cost-basis">
                  {formatCurrency(data.cost_basis, currency)}
                </div>
              </div>
            </div>

            {/* By-year breakdown */}
            {years.length > 0 && (
              <div className="rounded-xl border border-[#EBEBEA] p-3 mt-3">
                <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992] mb-2">Gasto por año</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {years.map(([year, total]) => (
                    <div key={year} data-testid={`renov-year-${year}`} className="rounded-lg bg-[#FAFAF9] p-2">
                      <div className="text-[11px] text-[#8A9992]">{year}</div>
                      <div className="font-display text-sm font-semibold tabular-nums text-[#0A110E]">
                        {formatCurrency(total, currency)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filters + item list */}
            {data.items && data.items.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">Filtrar</span>
                <select
                  data-testid="renov-filter-year"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="h-7 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs focus:outline-none focus:border-[#1C7C54]/60"
                >
                  <option value="all">Todos los años</option>
                  {years.map(([y]) => <option key={y} value={y}>{y}</option>)}
                </select>
                <select
                  data-testid="renov-filter-category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="h-7 px-2 rounded-md border border-[#EBEBEA] bg-white text-xs focus:outline-none focus:border-[#1C7C54]/60"
                >
                  <option value="all">Todas las categorías</option>
                  {availableCategories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <span
                  data-testid="renov-filter-total"
                  className="ml-auto text-xs text-[#5F7368]"
                >
                  {filteredItems.length} mov · {formatCurrency(filteredTotal, currency)}
                </span>
              </div>
            )}

            <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-2" data-testid="renov-list">
              {(!filteredItems || filteredItems.length === 0) ? (
                <div className="p-8 text-center text-sm text-[#8A9992]">
                  <Home className="w-8 h-8 mx-auto text-[#EBEBEA] mb-2" />
                  <div>
                    {(!data.items || data.items.length === 0)
                      ? "Aún no has vinculado ningún gasto a este inmueble."
                      : "Sin resultados para el filtro actual."}
                  </div>
                  {(!data.items || data.items.length === 0) && (
                    <div className="text-xs mt-1">
                      Vincula transacciones desde el detalle de la cuenta bancaria.
                    </div>
                  )}
                </div>
              ) : (
                <ul className="divide-y divide-[#EBEBEA]">
                  {filteredItems.map((t) => (
                    <li key={t.id} className="py-2 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#0A110E] truncate">
                          {t.description || "Gasto"}
                        </div>
                        <div className="text-[11px] text-[#8A9992]">
                          {t.date}
                          {t.category && ` · ${t.category}`}
                          {t.account_bank && ` · ${t.account_bank}`}
                          {t.account_alias && ` — ${t.account_alias}`}
                        </div>
                      </div>
                      <div className={`text-sm font-semibold tabular-nums shrink-0 ${
                        t.amount < 0 ? "text-[#D95D39]" : "text-[#1C7C54]"
                      }`}>
                        {formatCurrency(t.amount, t.currency || currency)}
                      </div>
                      <button
                        type="button"
                        onClick={() => unlink(t.id)}
                        title="Quitar vínculo"
                        data-testid={`renov-unlink-${t.id}`}
                        className="p-1 rounded hover:bg-[#EBEBEA] text-[#8A9992] shrink-0"
                      >
                        <Link2Off className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
